
import React, { useState, useEffect } from 'react';
import { User, UserRole, Order, ServiceCategory, OrderStatus, TopUpRequest, MillingCenter, DesignerServiceRate, Transaction, PricePlan, PricePlanItem, SystemSettings } from '../types';
import { Button, Card, Input, StatusBadge } from './ui';
import { DesignerDetailView } from './DesignerDetailView';
import { DoctorDetailView } from './DoctorDetailView';
import { Trash2, UserPlus, DollarSign, Plus, X, Check, AlertCircle, Search, List, Coins, Cpu, Anchor, Activity, FileText, Calculator, ChevronDown, ChevronUp, Tag, Eye, Pencil, Settings as SettingsIcon } from 'lucide-react';
import { api } from '../services/api';
import { translations, Language } from '../services/translations';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';

interface AdminPanelProps {
   lang: Language;
   orders: Order[];
   activeSection?: string; // Controlled by Parent (App -> Layout)
   onOrderClick: (order: Order) => void;
}

const RateInput = ({ serviceId, designerId, initialRate, onSave }: { serviceId: string, designerId: string, initialRate?: number, onSave: (dId: string, sId: string, val: number) => Promise<void> }) => {
   const [val, setVal] = useState(initialRate?.toString() || '');
   const [saving, setSaving] = useState(false);

   useEffect(() => { setVal(initialRate?.toString() || ''); }, [initialRate]);

   const handleBlur = async () => {
      const numVal = parseFloat(val);
      if (!isNaN(numVal) && numVal !== initialRate) {
         setSaving(true);
         await onSave(designerId, serviceId, numVal);
         setSaving(false);
      }
   };

   return (
      <input
         className={`w-20 bg-black/20 border border-zinc-700 rounded px-2 py-1 text-sm text-right focus:border-blue-500 outline-none transition-all ${saving ? 'border-green-500 text-green-400' : 'text-zinc-300 focus:text-white'}`}
         value={val}
         onChange={(e) => setVal(e.target.value)}
         onBlur={handleBlur}
         placeholder="0.00"
      />
   );
};

export const AdminPanel = ({ lang, orders, activeSection = 'overview', onOrderClick }: AdminPanelProps) => {
   // Map 'dashboard' to 'overview' for internal logic if needed
   const currentView = activeSection === 'dashboard' ? 'overview' : activeSection;

   const [users, setUsers] = useState<User[]>([]);
   const [services, setServices] = useState<ServiceCategory[]>([]);
   const [topUpRequests, setTopUpRequests] = useState<TopUpRequest[]>([]);
   const [millingCenters, setMillingCenters] = useState<MillingCenter[]>([]);
   const [pricePlans, setPricePlans] = useState<PricePlan[]>([]);
   const [transactions, setTransactions] = useState<Transaction[]>([]);
   const [designerRates, setDesignerRates] = useState<DesignerServiceRate[]>([]);
   const [systemSettings, setSystemSettings] = useState<SystemSettings | null>(null);

   // Dashboard State
   const [implantSearch, setImplantSearch] = useState('');

   // Bulk Delete State
   const [selectedOrders, setSelectedOrders] = useState<string[]>([]);

   // User Management State
   const [roleFilter, setRoleFilter] = useState<UserRole | 'ALL'>('ALL');

   // Milling Center State
   const [expandedCenter, setExpandedCenter] = useState<string | null>(null);

   // Price Plan State
   const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
   const [planItems, setPlanItems] = useState<PricePlanItem[]>([]);
   const [newPlanName, setNewPlanName] = useState('');

   // Form States
   const [newCategory, setNewCategory] = useState('');
   const [newService, setNewService] = useState({ name: '', price: '', color: '#3b82f6' });
   const [addingServiceTo, setAddingServiceTo] = useState<string | null>(null);
   const [editingServiceId, setEditingServiceId] = useState<string | null>(null);
   const [editForm, setEditForm] = useState({ name: '', price: '', color: '' });
   const [newCenter, setNewCenter] = useState({ name: '', whatsappNumber: '', servicesList: '', pricingInfo: '' });
   const [balanceAdjustment, setBalanceAdjustment] = useState<{ userId: string, amount: string, reason: string } | null>(null);
   const [userToDelete, setUserToDelete] = useState<User | null>(null);

   // Designer Detail View State
   const [selectedDesigner, setSelectedDesigner] = useState<User | null>(null);
   const [selectedDentist, setSelectedDentist] = useState<User | null>(null);

   useEffect(() => {
      refreshData();
   }, []);

   // Fetch plan items when selected
   useEffect(() => {
      if (selectedPlan) {
         api.getPricePlanItems(selectedPlan).then(setPlanItems);
      }
   }, [selectedPlan]);

   const refreshData = () => {
      api.getUsers().then(setUsers);
      api.getServices().then(setServices);
      api.getPricePlans().then(setPricePlans);
      api.getTopUpRequests().then(setTopUpRequests);
      api.getMillingCenters().then(setMillingCenters);
      api.getPricePlans().then(setPricePlans);
      api.getTransactions().then(setTransactions);
      api.getAllDesignerRates().then(setDesignerRates);
      api.getSettings().then(setSystemSettings);
   };

   // --- ACTIONS ---
   const handleAddCategory = async () => {
      if (!newCategory) return;
      await api.createCategory(newCategory);
      setNewCategory('');
      refreshData();
   };

   const handleAddService = async (categoryId: string) => {
      if (!newService.name || !newService.price) return;
      await api.createService(categoryId, newService.name, parseFloat(newService.price), newService.color);
      setNewService({ name: '', price: '', color: '#3b82f6' });
      setAddingServiceTo(null);
      refreshData();
   };

   const handleEditService = (service: any) => {
      setEditingServiceId(service.id);
      setEditForm({ name: service.name, price: service.price.toString(), color: service.color || '#3b82f6' });
   };

   const handleSaveService = async () => {
      if (!editingServiceId || !editForm.name || !editForm.price) return;
      await api.updateService(editingServiceId, {
         name: editForm.name,
         price: parseFloat(editForm.price),
         color: editForm.color
      });
      setEditingServiceId(null);
      refreshData();
   };

   const handleProcessTopUp = async (req: TopUpRequest, status: 'APPROVED' | 'REJECTED') => {
      if (confirm(`Are you sure you want to ${status} this request?`)) {
         await api.processTopUp(req.id, status);
         refreshData();
      }
   };

   const handleAddMillingCenter = async () => {
      if (!newCenter.name || !newCenter.whatsappNumber) return;
      await api.createMillingCenter(newCenter, []);
      setNewCenter({ name: '', whatsappNumber: '', servicesList: '', pricingInfo: '' });
      refreshData();
   };

   const handleUpdateMillingOffering = async (centerId: string, serviceId: string, cost: number) => {
      await api.updateMillingOffering(centerId, serviceId, cost);
      // Optimistic update
      setMillingCenters(prev => prev.map(c => {
         if (c.id !== centerId) return c;
         const offerings = c.offerings ? [...c.offerings] : [];
         const idx = offerings.findIndex(o => o.serviceId === serviceId);
         if (idx >= 0) offerings[idx].cost = cost;
         else offerings.push({ serviceId, cost });
         return { ...c, offerings };
      }));
   };

   const handleDeleteService = async (id: string, name: string) => {
      if (!confirm(`Are you sure you want to delete ${name}?`)) return;
      try {
         await api.deleteService(id);
         refreshData();
      } catch (error: any) {
         if (error.code === '23503') {
            alert("Cannot delete this service because it is referenced in existing orders. Consider archiving it instead if implemented, or remove it from orders first.");
         } else {
            console.error(error);
            alert("Failed to delete service. See console for details.");
         }
      }
   };

   const handleDeleteCategory = async (id: string, name: string) => {
      if (!confirm(`Are you sure you want to delete category ${name} and all its services?`)) return;
      try {
         await api.deleteCategory(id);
         refreshData();
      } catch (error: any) {
         console.error(error);
         alert("Failed to delete category. It may contain services referenced in orders.");
      }
   };

   const handleCreatePricePlan = async () => {
      if (!newPlanName) return;
      await api.createPricePlan(newPlanName, []);
      setNewPlanName('');
      refreshData();
   };

   const handleUpdatePlanPrice = async (serviceId: string, price: number) => {
      if (!selectedPlan) return;
      await api.updatePricePlanItems(selectedPlan, serviceId, price);
      setPlanItems(prev => {
         const exists = prev.find(i => i.serviceId === serviceId);
         if (exists) return prev.map(i => i.serviceId === serviceId ? { ...i, price } : i);
         return [...prev, { id: 'temp', planId: selectedPlan, serviceId, price }];
      });
   };

   const handleAdjustBalance = async () => {
      if (!balanceAdjustment) return;
      await api.adjustUserBalance(
         balanceAdjustment.userId,
         parseFloat(balanceAdjustment.amount),
         balanceAdjustment.reason,
         'ADJUSTMENT'
      );
      setBalanceAdjustment(null);
      refreshData();
   };

   const handleRateUpdate = async (designerId: string, serviceId: string, rate: number) => {
      await api.upsertDesignerRate(designerId, serviceId, rate);
      refreshData();
   };

   const handleDeleteUser = async () => {
      if (!userToDelete) return;
      try {
         await api.deleteUser(userToDelete.id);
         setUserToDelete(null);
         refreshData();
         alert("User deleted successfully");
      } catch (error: any) {
         console.error("Delete user error:", error);
         alert("Failed to delete user: " + error.message);
      }
   };

   const t = translations[lang];

   const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
         setSelectedOrders(orders.map(o => o.id));
      } else {
         setSelectedOrders([]);
      }
   };

   const handleSelectRow = (id: string) => {
      setSelectedOrders(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
   };

   const handleBulkDelete = async () => {
      if (!confirm(`Are you sure you want to delete ${selectedOrders.length} orders? This action cannot be undone.`)) return;
      try {
         await api.bulkDeleteOrders(selectedOrders);
         setSelectedOrders([]);
         refreshData();
      } catch (error) {
         console.error(error);
         alert("Failed to delete orders.");
      }
   };

   // --- RENDERERS ---

   const renderOverview = () => {
      // Removed Total Revenue Calculation
      const activeCount = orders.filter(o => o.status !== OrderStatus.DELIVERED && o.status !== OrderStatus.CANCELLED).length;
      const pendingPayments = topUpRequests.filter(r => r.status === 'PENDING').length;

      // Implant Tracker Logic
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const implantRows: any[] = [];
      orders.forEach(order => {
         if (new Date(order.createdAt) < thirtyDaysAgo) return;
         order.items.forEach(item => {
            const textToSearch = (item.serviceName + ' ' + (item.details || '') + ' ' + order.notes).toLowerCase();
            if (textToSearch.match(/implant|abutment|zircon|screw|cement|ti-base/)) {
               if (implantSearch && !textToSearch.includes(implantSearch.toLowerCase()) && !order.patientName.toLowerCase().includes(implantSearch.toLowerCase())) return;
               implantRows.push({
                  order,
                  item,
                  details: item.details || order.notes
               });
            }
         });
      });

      return (
         <div className="space-y-8 animate-fade-in">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
               {/* Removed Revenue Card */}
               <Card className="bg-zinc-900 border-zinc-800 p-6">
                  <div className="flex items-center gap-4">
                     <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-500"><Activity size={24} /></div>
                     <div><p className="text-zinc-500 text-xs font-bold uppercase">{t.dashboard.activeCases}</p><h3 className="text-2xl font-bold text-white">{activeCount}</h3></div>
                  </div>
               </Card>
               <Card className="bg-zinc-900 border-zinc-800 p-6">
                  <div className="flex items-center gap-4">
                     <div className="w-12 h-12 rounded-full bg-yellow-500/10 flex items-center justify-center text-yellow-500"><AlertCircle size={24} /></div>
                     <div><p className="text-zinc-500 text-xs font-bold uppercase">{t.dashboard.pendingPayments}</p><h3 className="text-2xl font-bold text-white">{pendingPayments}</h3></div>
                  </div>
               </Card>
               <Card className="bg-zinc-900 border-zinc-800 p-6">
                  <div className="flex items-center gap-4">
                     <div className="w-12 h-12 rounded-full bg-purple-500/10 flex items-center justify-center text-purple-500"><UserPlus size={24} /></div>
                     <div><p className="text-zinc-500 text-xs font-bold uppercase">{t.dashboard.newClients}</p><h3 className="text-2xl font-bold text-white">{users.filter(u => new Date().getTime() - 2592000000 < 0).length}</h3></div>
                  </div>
               </Card>
            </div>

            <div className="space-y-4">
               <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-zinc-900 border border-zinc-800 p-4 rounded-xl">
                  <div className="flex items-center gap-4">
                     <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center text-green-500"><SettingsIcon size={20} /></div>
                     <div>
                        <h3 className="font-bold text-white text-sm">Automated Notifications</h3>
                        <p className="text-zinc-500 text-xs">Daily deadline checks</p>
                     </div>
                  </div>

                  <div className="flex items-center gap-4">
                     <div className="flex items-center gap-2">
                        <span className="text-sm text-zinc-400">Send at (Egypt Time):</span>
                        <select
                           className="bg-black/30 border border-zinc-700 rounded px-2 py-1 text-sm text-white focus:border-blue-500 outline-none"
                           value={systemSettings?.notification_time?.toString() || '20'}
                           onChange={async (e) => {
                              const val = parseInt(e.target.value);
                              await api.updateSettings({ notification_time: val });
                              refreshData();
                           }}
                        >
                           {Array.from({ length: 24 }, (_, i) => (
                              <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                           ))}
                        </select>
                     </div>

                     <Button
                        variant="outline"
                        onClick={async () => {
                           try {
                              await api.triggerDeadlineCheck(true);
                              alert("Deadline check triggered successfully! Check your WhatsApp.");
                           } catch (e) {
                              alert("Failed to trigger check. See console.");
                           }
                        }}
                        className="border-green-500/30 hover:bg-green-500/10 text-green-400"
                     >
                        <span className="mr-2">📲</span> Test Now
                     </Button>
                  </div>
               </div>

               <div className="flex flex-col md:flex-row justify-between items-end md:items-center gap-4">
                  <div>
                     <h2 className="text-xl font-bold text-white flex items-center gap-2"><Anchor className="text-blue-400" size={24} /> {t.dashboard.implantTracker}</h2>
                     <p className="text-zinc-400 text-sm">{t.dashboard.implantTrackerDesc}</p>
                  </div>
                  <div className="relative">
                     <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
                     <input className="bg-zinc-900 border border-zinc-800 rounded-lg pl-10 pr-4 py-2 text-sm text-white focus:border-blue-500 outline-none w-64" placeholder={t.dashboard.searchImplant} value={implantSearch} onChange={(e) => setImplantSearch(e.target.value)} />
                  </div>
               </div>
               <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden shadow-lg">
                  <div className="overflow-x-auto">
                     <table className="w-full text-left text-sm">
                        <thead>
                           <tr className="bg-zinc-950 border-b border-zinc-800 text-zinc-500 font-bold text-xs uppercase tracking-wider">
                              <th className="p-4">{t.dashboard.tableHeaders.date}</th>
                              <th className="p-4">{t.dashboard.tableHeaders.patient} / ID</th>
                              <th className="p-4">{t.dashboard.tableHeaders.doctor}</th>
                              <th className="p-4">{t.dashboard.tableHeaders.tooth}</th>
                              <th className="p-4">{t.dashboard.tableHeaders.service}</th>
                              <th className="p-4 w-1/3">{t.dashboard.tableHeaders.details}</th>
                              <th className="p-4 text-right">{t.dashboard.tableHeaders.status}</th>
                           </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-800">
                           {implantRows.length === 0 ? (<tr><td colSpan={7} className="p-8 text-center text-zinc-500">{t.dashboard.noImplantCases}</td></tr>) : (
                              implantRows.map((row: any, i) => (
                                 <tr key={i} onClick={() => onOrderClick(row.order)} className="hover:bg-zinc-800/50 transition-colors group cursor-pointer">
                                    <td className="p-4 text-zinc-400 whitespace-nowrap">{new Date(row.order.createdAt).toLocaleDateString()}</td>
                                    <td className="p-4"><div className="font-bold text-white">{row.order.patientName}</div><div className="text-xs text-zinc-500 font-mono">#{row.order.id.slice(0, 6)}</div></td>
                                    <td className="p-4 text-zinc-300">{row.order.doctorName}</td>
                                    <td className="p-4"><span className="bg-blue-900/30 text-blue-400 px-2 py-1 rounded font-mono font-bold text-xs border border-blue-500/30">#{row.item.teeth.join(', ')}</span></td>
                                    <td className="p-4 text-zinc-300 font-medium">{row.item.serviceName}</td>
                                    <td className="p-4">{row.details ? <div className="bg-black/30 p-2 rounded border border-zinc-700/50 text-zinc-200 text-xs leading-relaxed">{row.details}</div> : <span className="text-zinc-600 italic">No details</span>}</td>
                                    <td className="p-4 text-right"><StatusBadge status={row.order.status} /></td>
                                 </tr>
                              ))
                           )}
                        </tbody>
                     </table>
                  </div>
               </div>
            </div>
         </div>
      );
   };

   const renderDesignerRates = () => {
      const designers = users.filter(u => u.role === UserRole.TECHNICIAN);
      return (
         <div className="space-y-6 animate-fade-in">
            <div className="flex justify-between items-center">
               <div>
                  <h2 className="text-2xl font-bold text-white mb-1">{t.dashboard.designerMatrix}</h2>
                  <p className="text-zinc-400">{t.dashboard.designerMatrixDesc}</p>
               </div>
            </div>

            <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden shadow-lg overflow-x-auto">
               <table className="w-full text-left text-sm border-collapse">
                  <thead>
                     <tr className="bg-zinc-950 text-zinc-500 text-xs font-bold uppercase">
                        <th className="p-4 border-r border-zinc-800 sticky left-0 bg-zinc-950 z-10 w-64">{t.dashboard.tableHeaders.serviceName}</th>
                        <th className="p-4 border-r border-zinc-800 w-32 text-center bg-zinc-950/50">{t.dashboard.tableHeaders.basePrice}</th>
                        {designers.map(d => (
                           <th key={d.id} className="p-4 text-center min-w-[120px] border-r border-zinc-800/50">
                              <div className="text-white">{d.name}</div>
                              <div className="text-[10px] font-normal lowercase">{d.email}</div>
                           </th>
                        ))}
                     </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800">
                     {services.flatMap(cat => cat.services.map(srv => ({ ...srv, category: cat.name }))).map(srv => (
                        <tr key={srv.id} className="hover:bg-zinc-800/30 transition-colors">
                           <td className="p-4 border-r border-zinc-800 sticky left-0 bg-zinc-900 font-medium text-zinc-200">
                              {srv.name}
                              <div className="text-[10px] text-zinc-500 uppercase">{srv.category}</div>
                           </td>
                           <td className="p-4 border-r border-zinc-800 text-center text-zinc-500 font-mono">{srv.price}</td>
                           {designers.map(d => {
                              const rate = designerRates.find(r => r.designerId === d.id && r.serviceId === srv.id);
                              return (
                                 <td key={d.id} className="p-2 border-r border-zinc-800/30 text-center">
                                    <RateInput
                                       serviceId={srv.id}
                                       designerId={d.id}
                                       initialRate={rate?.rate}
                                       onSave={handleRateUpdate}
                                    />
                                 </td>
                              );
                           })}
                        </tr>
                     ))}
                  </tbody>
               </table>
            </div>
         </div>
      );
   };

   const renderFinancials = () => {
      // Chart Data
      const last6Months = Array.from({ length: 6 }, (_, i) => {
         const d = new Date();
         d.setMonth(d.getMonth() - i);
         return d.toLocaleString('default', { month: 'short' });
      }).reverse();

      const chartData = last6Months.map(month => {
         return {
            name: month,
            revenue: orders.filter(o => o.status === 'DELIVERED' && new Date(o.createdAt).toLocaleString('default', { month: 'short' }) === month).reduce((s, o) => s + o.price, 0),
            payouts: Math.abs(transactions.filter(t => t.type === 'DESIGNER_PAYOUT' && new Date(t.createdAt).toLocaleString('default', { month: 'short' }) === month).reduce((s, t) => s + t.amount, 0))
         };
      });

      // Payout Calculator
      const technicians = users.filter(u => u.role === UserRole.TECHNICIAN);

      return (
         <div className="space-y-8 animate-fade-in">
            <h2 className="text-2xl font-bold text-white">Financial Intelligence</h2>

            {/* Chart */}
            <div className="h-80 bg-zinc-900 border border-zinc-800 rounded-xl p-6">
               <h3 className="text-sm font-bold text-zinc-500 uppercase mb-4">Revenue vs Payouts (6 Months)</h3>
               <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                     <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                     <XAxis dataKey="name" stroke="#666" />
                     <YAxis stroke="#666" />
                     <Tooltip contentStyle={{ backgroundColor: '#18181b', borderColor: '#27272a', color: '#fff' }} />
                     <Legend />
                     <Bar dataKey="revenue" fill="#3b82f6" name="Revenue" radius={[4, 4, 0, 0]} />
                     <Bar dataKey="payouts" fill="#ef4444" name="Designer Payouts" radius={[4, 4, 0, 0]} />
                  </BarChart>
               </ResponsiveContainer>
            </div>

            {/* Designer Payouts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
               <div className="space-y-4">
                  <h3 className="text-xl font-bold text-white flex items-center gap-2"><Calculator size={20} /> Designer Payouts (Est.)</h3>
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                     <table className="w-full text-left text-sm">
                        <thead className="bg-zinc-950 text-zinc-500 text-xs font-bold uppercase">
                           <tr>
                              <th className="p-4">Technician</th>
                              <th className="p-4 text-right">Cases (This Month)</th>
                              <th className="p-4 text-right">Est. Owed</th>
                           </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-800">
                           {technicians.map(tech => {
                              const techOrders = orders.filter(o => o.technicianId === tech.id && o.status === 'DELIVERED');
                              let owed = 0;
                              techOrders.forEach(o => {
                                 o.items.forEach(item => {
                                    const rate = designerRates.find(r => r.designerId === tech.id && r.serviceId === item.serviceId)?.rate || 0;
                                    owed += rate * item.teeth.length;
                                 });
                              });
                              return (
                                 <tr key={tech.id} className="hover:bg-zinc-800/50">
                                    <td className="p-4 font-medium text-white">{tech.name}</td>
                                    <td className="p-4 text-right">{techOrders.length}</td>
                                    <td className="p-4 text-right font-mono font-bold text-green-400">{owed.toLocaleString()} EGP</td>
                                 </tr>
                              );
                           })}
                        </tbody>
                     </table>
                  </div>
               </div>

               {/* Transactions Log */}
               <div className="space-y-4">
                  <h3 className="text-xl font-bold text-white flex items-center gap-2"><List size={20} /> Recent Transactions</h3>
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden h-[300px] overflow-y-auto custom-scrollbar">
                     <table className="w-full text-left text-sm">
                        <thead className="bg-zinc-950 text-zinc-500 text-xs font-bold uppercase sticky top-0">
                           <tr>
                              <th className="p-4">Date</th>
                              <th className="p-4">Type</th>
                              <th className="p-4 text-right">Amount</th>
                           </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-800">
                           {transactions.map(t => (
                              <tr key={t.id}>
                                 <td className="p-4 text-zinc-500">{new Date(t.createdAt).toLocaleDateString()}</td>
                                 <td className="p-4 text-zinc-300">{t.description}</td>
                                 <td className={`p-4 text-right font-mono font-bold ${t.amount > 0 ? 'text-green-400' : 'text-red-400'}`}>{t.amount}</td>
                              </tr>
                           ))}
                        </tbody>
                     </table>
                  </div>
               </div>
            </div>
         </div>
      );
   };

   const renderUsers = () => (
      <div className="space-y-4 animate-fade-in">
         <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
               <h2 className="text-2xl font-bold text-white">Users & Clients</h2>
               <p className="text-zinc-400 text-sm">Manage user roles and balances.</p>
            </div>

            <div className="flex gap-4">
               <select
                  className="bg-zinc-900 border border-zinc-800 rounded-lg px-4 py-2 text-sm text-white outline-none focus:border-blue-500 cursor-pointer"
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value as UserRole | 'ALL')}
               >
                  <option value="ALL">All Roles</option>
                  <option value={UserRole.DENTIST}>Dentists</option>
                  <option value={UserRole.TECHNICIAN}>Technicians</option>
                  <option value={UserRole.ADMIN}>Admins</option>
                  <option value={UserRole.MODERATOR}>Moderators</option>
               </select>
               <Button onClick={() => { }}><UserPlus size={18} /> Invite User</Button>
            </div>
         </div>

         <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
            <table className="w-full text-left text-sm">
               <thead className="bg-zinc-950 text-zinc-500 text-xs font-bold uppercase">
                  <tr>
                     <th className="p-4">Name</th>
                     <th className="p-4">Role</th>
                     <th className="p-4">Clinic</th>
                     <th className="p-4">Price Plan</th>
                     <th className="p-4 text-right">Balance</th>
                     <th className="p-4 text-right">Actions</th>
                  </tr>
               </thead>
               <tbody className="divide-y divide-zinc-800">
                  {users.filter(u => roleFilter === 'ALL' || u.role === roleFilter).map(u => (
                     <tr key={u.id} className="hover:bg-zinc-800/50">
                        <td className="p-4 font-medium text-white">{u.name}<div className="text-xs text-zinc-500">{u.email}</div></td>
                        <td className="p-4"><span className={`px-2 py-1 rounded text-xs font-bold ${u.role === 'ADMIN' ? 'bg-purple-900/30 text-purple-400' : u.role === 'TECHNICIAN' ? 'bg-orange-900/30 text-orange-400' : 'bg-blue-900/30 text-blue-400'}`}>{u.role}</span></td>
                        <td className="p-4 text-zinc-400">{u.clinicName || '-'}</td>
                        <td className="p-4">
                           {u.role === UserRole.DENTIST ? (
                              <select
                                 className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-xs text-white outline-none focus:border-blue-500 cursor-pointer w-full max-w-[150px]"
                                 value={u.pricePlanId || ''}
                                 onChange={async (e) => {
                                    const planId = e.target.value;
                                    await api.updateUserPricePlan(u.id, planId);
                                    refreshData();
                                 }}
                              >
                                 <option value="" disabled>Select Plan</option>
                                 {pricePlans.map(plan => (
                                    <option key={plan.id} value={plan.id}>{plan.name} {plan.isDefault ? '(Default)' : ''}</option>
                                 ))}
                              </select>
                           ) : (
                              <span className="text-zinc-600 text-xs italic">N/A</span>
                           )}
                        </td>
                        <td className={`p-4 text-right font-mono font-bold ${u.balance < 0 ? 'text-red-400' : 'text-green-400'}`}>{u.balance}</td>
                        <td className="p-4 text-right flex gap-2 justify-end">
                           {u.role === UserRole.TECHNICIAN && (
                              <Button size="sm" onClick={() => setSelectedDesigner(u)}>
                                 <Eye size={16} className="mr-1" /> Manage
                              </Button>
                           )}
                           {u.role === UserRole.DENTIST && (
                              <Button size="sm" onClick={() => setSelectedDentist(u)}>
                                 <Eye size={16} className="mr-1" /> Manage
                              </Button>
                           )}
                           <Button size="sm" variant="secondary" onClick={() => setBalanceAdjustment({ userId: u.id, amount: '', reason: '' })}>Adjust Balance</Button>
                           <Button size="sm" variant="secondary" className="hover:bg-red-900/40 hover:text-red-400 border-zinc-800" onClick={() => setUserToDelete(u)}>
                              <Trash2 size={16} />
                           </Button>
                        </td>
                     </tr>
                  ))}
               </tbody>
            </table>
         </div>

         {balanceAdjustment && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[200]">
               <div className="bg-zinc-900 border border-zinc-800 p-6 rounded-xl w-96 space-y-4 shadow-2xl">
                  <h3 className="font-bold text-white text-lg">Adjust Balance</h3>
                  <p className="text-sm text-zinc-400">Manually credit or charge a user's wallet.</p>
                  <Input label="Amount (+ Credit / - Charge)" type="number" value={balanceAdjustment.amount} onChange={(e: any) => setBalanceAdjustment({ ...balanceAdjustment, amount: e.target.value })} autoFocus />
                  <Input label="Reason / Note" value={balanceAdjustment.reason} onChange={(e: any) => setBalanceAdjustment({ ...balanceAdjustment, reason: e.target.value })} placeholder="e.g. Manual Payment" />
                  <div className="flex justify-end gap-2 pt-2">
                     <Button variant="secondary" onClick={() => setBalanceAdjustment(null)}>Cancel</Button>
                     <Button onClick={handleAdjustBalance}>Confirm Adjustment</Button>
                  </div>
               </div>
            </div>
         )}

         {userToDelete && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[200]">
               <div className="bg-zinc-900 border border-zinc-800 p-6 rounded-xl w-96 space-y-4 shadow-2xl animate-fade-in">
                  <div className="w-12 h-12 rounded-full bg-red-900/20 flex items-center justify-center mx-auto mb-2 text-red-500">
                     <Trash2 size={24} />
                  </div>
                  <div className="text-center">
                     <h3 className="font-bold text-white text-lg">Delete User?</h3>
                     <p className="text-sm text-zinc-400 mt-1">
                        Are you sure you want to delete <strong>{userToDelete.name}</strong>?
                        This will permanently remove their account from the system.
                     </p>
                     <div className="mt-4 p-3 bg-red-900/10 border border-red-900/20 rounded text-[11px] text-red-400 text-left">
                        <strong>Warning:</strong> Historical orders will be preserved, but the doctor/technician name on those orders will be cleared.
                     </div>
                  </div>
                  <div className="flex flex-col gap-2 mt-4">
                     <Button onClick={handleDeleteUser} className="bg-red-600 hover:bg-red-500">Yes, Delete User</Button>
                     <Button variant="secondary" onClick={() => setUserToDelete(null)}>Cancel</Button>
                  </div>
               </div>
            </div>
         )}
      </div>
   );

   const renderMilling = () => (
      <div className="space-y-6 animate-fade-in">
         <h2 className="text-2xl font-bold text-white">Milling Centers & Costs</h2>
         <div className="space-y-4">
            {millingCenters.map(center => (
               <div key={center.id} className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                  <div
                     className="p-4 flex justify-between items-center cursor-pointer hover:bg-zinc-800/50 transition-colors"
                     onClick={() => setExpandedCenter(expandedCenter === center.id ? null : center.id)}
                  >
                     <div className="flex items-center gap-4">
                        <Cpu size={20} className="text-purple-400" />
                        <div>
                           <h4 className="font-bold text-white">{center.name}</h4>
                           <p className="text-xs text-zinc-500">{center.whatsappNumber}</p>
                        </div>
                     </div>
                     <div className="flex items-center gap-4">
                        <div className="text-sm text-zinc-400">{center.offerings?.length || 0} Offerings Configured</div>
                        {expandedCenter === center.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                     </div>
                  </div>

                  {expandedCenter === center.id && (
                     <div className="border-t border-zinc-800 bg-zinc-950/50 p-4">
                        <h5 className="text-xs font-bold text-zinc-500 uppercase mb-3">Service Cost Configuration</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                           {services.flatMap(cat => cat.services.map(srv => ({ ...srv, category: cat.name }))).map(srv => {
                              const offering = center.offerings?.find(o => o.serviceId === srv.id);
                              return (
                                 <div key={srv.id} className="flex justify-between items-center bg-zinc-900 p-3 rounded-lg border border-zinc-800">
                                    <div>
                                       <div className="text-sm text-zinc-200">{srv.name}</div>
                                       <div className="text-[10px] text-zinc-500 uppercase">{srv.category}</div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                       <span className="text-xs text-zinc-600">Cost:</span>
                                       <input
                                          className="w-16 bg-black/30 border border-zinc-700 rounded px-2 py-1 text-sm text-right text-white focus:border-blue-500 outline-none"
                                          placeholder="0"
                                          defaultValue={offering?.cost}
                                          onBlur={(e) => handleUpdateMillingOffering(center.id, srv.id, parseFloat(e.target.value) || 0)}
                                       />
                                    </div>
                                 </div>
                              );
                           })}
                        </div>
                     </div>
                  )}
               </div>
            ))}

            <div className="bg-zinc-900 border border-zinc-800 p-4 rounded-xl space-y-3">
               <h4 className="font-bold text-white text-sm uppercase">Add New Partner</h4>
               <div className="grid grid-cols-2 gap-2">
                  <input className="bg-black/30 border border-zinc-700 rounded px-3 py-2 text-white text-sm" placeholder="Name" value={newCenter.name} onChange={e => setNewCenter({ ...newCenter, name: e.target.value })} />
                  <input className="bg-black/30 border border-zinc-700 rounded px-3 py-2 text-white text-sm" placeholder="WhatsApp (e.g. +20...)" value={newCenter.whatsappNumber} onChange={e => setNewCenter({ ...newCenter, whatsappNumber: e.target.value })} />
               </div>
               <Button onClick={handleAddMillingCenter} className="w-full">Add Center</Button>
            </div>
         </div>
      </div>
   );

   const renderPricePlans = () => (
      <div className="space-y-6 animate-fade-in">
         <h2 className="text-2xl font-bold text-white">Price Plans</h2>

         <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Plan List */}
            <div className="space-y-4">
               <div className="flex gap-2">
                  <input
                     className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-white text-sm outline-none"
                     placeholder="New Plan Name"
                     value={newPlanName}
                     onChange={(e) => setNewPlanName(e.target.value)}
                  />
                  <Button size="sm" onClick={handleCreatePricePlan}><Plus size={16} /></Button>
               </div>

               <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                  {pricePlans.map(plan => (
                     <div
                        key={plan.id}
                        onClick={() => setSelectedPlan(plan.id)}
                        className={`p-4 flex justify-between items-center cursor-pointer transition-colors border-b border-zinc-800 last:border-0 ${selectedPlan === plan.id ? 'bg-blue-900/20 border-l-4 border-l-blue-500' : 'hover:bg-zinc-800'}`}
                     >
                        <div>
                           <div className="font-bold text-white">{plan.name}</div>
                           {plan.isDefault && <span className="text-[10px] bg-green-900/20 text-green-400 px-2 py-0.5 rounded border border-green-500/20">Default</span>}
                        </div>
                        <div className="flex gap-2">
                           {!plan.isDefault && (
                              <button
                                 onClick={(e) => { e.stopPropagation(); api.setPlanAsDefault(plan.id).then(refreshData); }}
                                 className="text-zinc-500 hover:text-green-400 p-1"
                                 title="Set as Default"
                              >
                                 <Check size={14} />
                              </button>
                           )}
                           <button
                              onClick={(e) => { e.stopPropagation(); if (confirm('Delete plan?')) api.deletePricePlan(plan.id).then(refreshData); }}
                              className="text-zinc-500 hover:text-red-400 p-1"
                           >
                              <Trash2 size={14} />
                           </button>
                        </div>
                     </div>
                  ))}
               </div>
            </div>

            {/* Editor */}
            <div className="md:col-span-2 bg-zinc-900 border border-zinc-800 rounded-xl p-6">
               {!selectedPlan ? (
                  <div className="text-center text-zinc-500 py-20 flex flex-col items-center">
                     <Tag size={48} className="opacity-50 mb-4" />
                     <p>Select a price plan to edit services.</p>
                  </div>
               ) : (
                  <div className="space-y-4 h-full flex flex-col">
                     <div className="flex justify-between items-center border-b border-zinc-800 pb-4">
                        <h3 className="font-bold text-white">Editing: <span className="text-blue-400">{pricePlans.find(p => p.id === selectedPlan)?.name}</span></h3>
                     </div>

                     <div className="flex-1 overflow-y-auto max-h-[600px] pr-2 custom-scrollbar">
                        <table className="w-full text-left text-sm">
                           <thead className="bg-zinc-950 text-zinc-500 text-xs font-bold uppercase sticky top-0 z-10">
                              <tr>
                                 <th className="p-3">Service</th>
                                 <th className="p-3 text-right">Base Price</th>
                                 <th className="p-3 text-right">Plan Price</th>
                              </tr>
                           </thead>
                           <tbody className="divide-y divide-zinc-800">
                              {services.flatMap(cat => cat.services.map(srv => ({ ...srv, category: cat.name }))).map(srv => {
                                 const item = planItems.find(i => i.serviceId === srv.id);
                                 return (
                                    <tr key={srv.id} className="hover:bg-zinc-800/30">
                                       <td className="p-3">
                                          <div className="text-zinc-200">{srv.name}</div>
                                          <div className="text-[10px] text-zinc-500">{srv.category}</div>
                                       </td>
                                       <td className="p-3 text-right text-zinc-500 font-mono">{srv.price}</td>
                                       <td className="p-3 text-right">
                                          <input
                                             className={`w-24 bg-black/30 border border-zinc-700 rounded px-2 py-1 text-right text-white focus:border-blue-500 outline-none ${item ? 'text-green-400 font-bold' : ''}`}
                                             placeholder={srv.price.toString()}
                                             defaultValue={item?.price}
                                             onBlur={(e) => {
                                                const val = parseFloat(e.target.value);
                                                if (!isNaN(val)) handleUpdatePlanPrice(srv.id, val);
                                             }}
                                          />
                                       </td>
                                    </tr>
                                 );
                              })}
                           </tbody>
                        </table>
                     </div>
                  </div>
               )}
            </div>
         </div>
      </div>
   );

   if (selectedDesigner) {
      return (
         <div className="w-full h-full pb-20">
            <DesignerDetailView
               designer={selectedDesigner}
               orders={orders}
               rates={designerRates}
               transactions={transactions}
               services={services}
               onClose={() => setSelectedDesigner(null)}
               onRateUpdate={handleRateUpdate}
            />
         </div>
      );
   }

   if (selectedDentist) {
      return (
         <div className="w-full h-full pb-20">
            <DoctorDetailView
               doctor={selectedDentist}
               orders={orders}
               transactions={transactions}
               onClose={() => setSelectedDentist(null)}
            />
         </div>
      );
   }

   return (
      <div className="w-full h-full pb-20">
         <div className="min-h-[400px]">
            {currentView === 'overview' && renderOverview()}
            {currentView === 'all_orders' && (
               <div className="space-y-4">
                  <div className="flex justify-between items-center">
                     <h2 className="text-2xl font-bold text-white">All Orders</h2>
                     {selectedOrders.length > 0 && (
                        <div className="flex items-center gap-4 bg-blue-900/20 px-4 py-2 rounded-lg border border-blue-500/30 animate-fade-in">
                           <span className="text-blue-400 text-sm font-bold">{selectedOrders.length} Selected</span>
                           <Button size="sm" variant="danger" onClick={handleBulkDelete}><Trash2 size={16} /> Delete Selected</Button>
                        </div>
                     )}
                  </div>
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                     <table className="w-full text-left text-sm">
                        <thead className="bg-zinc-950 text-zinc-500 text-xs font-bold uppercase">
                           <tr>
                              <th className="p-4 w-10">
                                 <input type="checkbox"
                                    className="rounded border-zinc-700 bg-zinc-900 text-blue-500 focus:ring-offset-0 focus:ring-1 focus:ring-blue-500 cursor-pointer"
                                    checked={selectedOrders.length === orders.length && orders.length > 0}
                                    onChange={handleSelectAll}
                                 />
                              </th>
                              <th className="p-4">ID</th>
                              <th className="p-4">Patient</th>
                              <th className="p-4">Doctor</th>
                              <th className="p-4">Status</th>
                              <th className="p-4 text-right">Price</th>
                           </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-800">
                           {orders.map(o => (
                              <tr key={o.id} onClick={() => onOrderClick(o)} className={`hover:bg-zinc-800/50 cursor-pointer ${selectedOrders.includes(o.id) ? 'bg-blue-900/10' : ''}`}>
                                 <td className="p-4">
                                    <input type="checkbox"
                                       className="rounded border-zinc-700 bg-zinc-900 text-blue-500 focus:ring-offset-0 focus:ring-1 focus:ring-blue-500 cursor-pointer"
                                       checked={selectedOrders.includes(o.id)}
                                       onClick={(e) => e.stopPropagation()}
                                       onChange={() => handleSelectRow(o.id)}
                                    />
                                 </td>
                                 <td className="p-4 font-mono text-zinc-400">#{o.id.slice(0, 6)}</td>
                                 <td className="p-4 text-white font-bold">{o.patientName}</td>
                                 <td className="p-4 text-zinc-300">{o.doctorName}</td>
                                 <td className="p-4"><StatusBadge status={o.status} /></td>
                                 <td className="p-4 text-right font-mono">{o.price}</td>
                              </tr>
                           ))}
                        </tbody>
                     </table>
                  </div>
               </div>
            )}
            {currentView === 'users' && renderUsers()}
            {currentView === 'financials' && renderFinancials()}
            {currentView === 'rates' && renderDesignerRates()}
            {currentView === 'services' && (
               <div className="space-y-6 animate-fade-in">
                  <h2 className="text-2xl font-bold text-white">Service Catalog</h2>
                  <div className="grid gap-6">
                     {services.map(cat => (
                        <div key={cat.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                           <div className="flex justify-between items-center mb-4">
                              <h3 className="font-bold text-lg text-white">{cat.name}</h3>
                              <div className="flex gap-2">
                                 <Button size="sm" variant="secondary" onClick={() => setAddingServiceTo(cat.id)}><Plus size={16} /></Button>
                                 <Button size="sm" variant="danger" onClick={() => handleDeleteCategory(cat.id, cat.name)}><Trash2 size={16} /></Button>
                              </div>
                           </div>
                           <div className="space-y-2">
                              {cat.services.map(srv => (
                                 editingServiceId === srv.id ? (
                                    <div key={srv.id} className="flex flex-col gap-2 p-3 bg-zinc-950 rounded-lg border border-blue-500/50">
                                       <div className="flex gap-2 items-center">
                                          <input type="color" className="w-8 h-8 rounded bg-transparent cursor-pointer" value={editForm.color} onChange={e => setEditForm({ ...editForm, color: e.target.value })} />
                                          <input className="bg-transparent border-b border-blue-500/50 text-white text-sm px-2 py-1 outline-none w-full" value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} autoFocus />
                                          <input className="bg-transparent border-b border-blue-500/50 text-white text-sm px-2 py-1 outline-none w-24" type="number" value={editForm.price} onChange={e => setEditForm({ ...editForm, price: e.target.value })} />
                                       </div>
                                       <div className="flex justify-end gap-2">
                                          <Button size="sm" variant="secondary" onClick={() => setEditingServiceId(null)}>Cancel</Button>
                                          <Button size="sm" onClick={handleSaveService}>Save</Button>
                                       </div>
                                    </div>
                                 ) : (
                                    <div key={srv.id} className="flex justify-between items-center p-3 bg-zinc-950 rounded-lg border border-zinc-800 group">
                                       <div className="flex items-center gap-3">
                                          <div className="w-4 h-4 rounded-full border border-zinc-700" style={{ backgroundColor: srv.color || '#3b82f6' }}></div>
                                          <span className="text-zinc-300">{srv.name}</span>
                                       </div>
                                       <div className="flex items-center gap-3">
                                          <span className="font-mono text-zinc-500">{srv.price} EGP</span>
                                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                             <button onClick={() => handleEditService(srv)} className="p-1 hover:bg-zinc-800 rounded text-zinc-400 hover:text-blue-400 transition-colors"><Pencil size={14} /></button>
                                             <button onClick={() => handleDeleteService(srv.id, srv.name)} className="p-1 hover:bg-zinc-800 rounded text-zinc-400 hover:text-red-400 transition-colors"><Trash2 size={14} /></button>
                                          </div>
                                       </div>
                                    </div>
                                 )
                              ))}
                              {addingServiceTo === cat.id && (
                                 <div className="flex gap-2 items-center bg-blue-900/10 p-2 rounded-lg border border-blue-500/20">
                                    <input type="color" className="w-8 h-8 rounded bg-transparent cursor-pointer" value={newService.color} onChange={e => setNewService({ ...newService, color: e.target.value })} />
                                    <input className="bg-transparent border-b border-blue-500/50 text-white text-sm px-2 py-1 outline-none w-full" placeholder="Name" value={newService.name} onChange={e => setNewService({ ...newService, name: e.target.value })} autoFocus />
                                    <input className="bg-transparent border-b border-blue-500/50 text-white text-sm px-2 py-1 outline-none w-24" placeholder="Price" type="number" value={newService.price} onChange={e => setNewService({ ...newService, price: e.target.value })} />
                                    <button onClick={() => handleAddService(cat.id)} className="p-1 bg-blue-600 rounded text-white"><Check size={16} /></button>
                                    <button onClick={() => setAddingServiceTo(null)} className="p-1 text-zinc-500 hover:text-white"><X size={16} /></button>
                                 </div>
                              )}
                           </div>
                        </div>
                     ))}
                     <div className="flex gap-2 bg-zinc-900 p-4 rounded-xl border border-zinc-800">
                        <input className="bg-transparent w-full text-white outline-none" placeholder="Create New Category..." value={newCategory} onChange={e => setNewCategory(e.target.value)} />
                        <Button onClick={handleAddCategory} size="sm">Add Category</Button>
                     </div>
                  </div>
               </div>
            )}
            {currentView === 'price_plans' && renderPricePlans()}
            {currentView === 'milling' && renderMilling()}
            {currentView === 'requests' && (
               <div className="space-y-6 animate-fade-in">
                  <h2 className="text-2xl font-bold text-white">Top-Up Requests</h2>
                  <div className="grid gap-4">
                     {topUpRequests.map(req => (
                        <div key={req.id} className="bg-zinc-900 border border-zinc-800 p-4 rounded-xl flex justify-between items-center">
                           <div>
                              <h4 className="font-bold text-white">{req.doctorName}</h4>
                              <p className="text-zinc-500 text-sm">Amount: <span className="text-white font-mono">{req.amount} EGP</span></p>
                           </div>
                           <div className="flex items-center gap-4">
                              {req.proofUrl && <a href={req.proofUrl} target="_blank" className="text-blue-400 hover:underline text-sm flex items-center gap-1"><FileText size={14} /> Receipt</a>}
                              {req.status === 'PENDING' ? (
                                 <div className="flex gap-2">
                                    <Button size="sm" onClick={() => handleProcessTopUp(req, 'APPROVED')} className="bg-green-600 hover:bg-green-700 border-none">Approve</Button>
                                    <Button size="sm" variant="danger" onClick={() => handleProcessTopUp(req, 'REJECTED')}>Reject</Button>
                                 </div>
                              ) : (
                                 <StatusBadge status={req.status as any} />
                              )}
                           </div>
                        </div>
                     ))}
                  </div>
               </div>
            )}
         </div>
      </div>
   );
};
